import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import {
  Document,
  Folder,
  folder,
  id,
  Identifiable,
  document as d,
} from "@planzone/dto";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { State, Selector } from "./types";
import { initialState } from "./initialState";
import { createSelector } from "./selector";

function handleRequest(
  state: State,
  { namespace, type }: Boundaries["retrievePrivateDocuments"]["request"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToPending(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          type,
          namespace,
          request: setRequestStateToPending(),
          folder: { ...folder, uuid: id },
        },
      ];
}

function handleSuccess(
  state: State,
  {
    namespace,
    type,
    folder,
  }: Boundaries["retrievePrivateDocuments"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          folder,
          request: setRequestStateToSuccess(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          type,
          namespace,
          request: setRequestStateToSuccess(),
          folder,
        },
      ];
}

function handleFailure(
  state: State,
  { namespace, type }: Boundaries["retrievePrivateDocuments"]["failure"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          type,
          namespace,
          request: setRequestStateToFailure(),
          folder: { ...folder, uuid: id },
        },
      ];
}

function replaceDocument(
  folder: Identifiable<Folder>,
  document: Identifiable<Document>,
  target: Identifiable<Folder>,
): Identifiable<Folder> {
  return folder.uuid === target.uuid
    ? { ...folder, children: [...folder.children, document] }
    : {
        ...folder,
        children: folder.children.map((child) =>
          child.type === "folder"
            ? replaceDocument(child, document, target)
            : child,
        ),
      };
}

function updateDocument(
  folder: Identifiable<Folder>,
  document: Identifiable<Document>,
  target: Identifiable<Folder>,
): Identifiable<Folder> {
  return folder.uuid === target.uuid
    ? {
        ...folder,
        children: [
          ...folder.children.filter((d) => d.uuid !== document.uuid),
          document,
        ],
      }
    : {
        ...folder,
        children: folder.children.map((child) =>
          child.type === "folder"
            ? updateDocument(child, document, target)
            : child,
        ),
      };
}

function handleUpload(
  state: State,
  {
    namespace,
    type,
    folder,
    document,
  }: Boundaries["uploadDocument"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          folder: replaceDocument(state[index].folder, document, folder),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function replaceFolder(
  currentFolder: Identifiable<Folder>,
  folder: Identifiable<Folder>,
  target: Identifiable<Folder>,
): Identifiable<Folder> {
  return currentFolder.uuid === target.uuid
    ? { ...currentFolder, children: [...currentFolder.children, folder] }
    : {
        ...currentFolder,
        children: currentFolder.children.map((child) =>
          child.type === "folder"
            ? replaceFolder(child, folder, target)
            : child,
        ),
      };
}

function handleCreateFolder(
  state: State,
  {
    namespace,
    type,
    folder,
    parentFolder,
  }: Boundaries["createFolder"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          folder: replaceFolder(state[index].folder, folder, parentFolder),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function removeFolder(
  folder: Identifiable<Folder>,
  target: Identifiable<Folder>,
): Identifiable<Folder> {
  return {
    ...folder,
    children: folder.children
      .filter(({ uuid }) => uuid !== target.uuid)
      .map((child) =>
        child.type === "folder" ? removeFolder(child, target) : child,
      ),
  };
}

function handleRemoveFolder(
  state: State,
  folder: Boundaries["removeFolder"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    folder: removeFolder(req.folder, folder),
  }));
}

function removeDocument(
  folder: Identifiable<Folder>,
  target: Identifiable<Document>,
): Identifiable<Folder> {
  return {
    ...folder,
    children: folder.children
      .filter(({ uuid }) => uuid !== target.uuid)
      .map((child) =>
        child.type === "folder" ? removeDocument(child, target) : child,
      ),
  };
}

function handleRemoveDocument(
  state: State,
  document: Boundaries["removeDocument"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    folder: removeDocument(req.folder, document),
  }));
}

function insertFileIntoFolder(
  folder: Identifiable<Folder>,
  document: Identifiable<Document>,
  target: string,
): Identifiable<Folder> {
  return folder.uuid === target
    ? { ...folder, children: [...folder.children, document] }
    : {
        ...folder,
        children: folder.children.map((child) =>
          child.type === "folder"
            ? insertFileIntoFolder(child, document, target)
            : child,
        ),
      };
}

function handleMoveDocumentRequest(
  state: State,
  { document, from, to }: Boundaries["moveDocument"]["request"],
): State {
  const reqState = state.map((req) => ({
    ...req,
    folder: removeDocument(req.folder, {
      uuid: document.uuid,
      ...d,
    }),
  }));
  const index = reqState.findIndex((s) => s.request.isSuccessful);
  return [
    ...reqState.slice(0, index),
    {
      ...reqState[index],
      folder: insertFileIntoFolder(reqState[index].folder, document, to),
    },
    ...reqState.slice(index + 1),
  ];
}

function handleMoveDocumentFailure(
  state: State,
  { document, from, to }: Boundaries["moveDocument"]["failure"],
): State {
  const reqState = state.map((req) => ({
    ...req,
    folder: removeDocument(req.folder, {
      uuid: document.uuid,
      ...d,
    }),
  }));
  const index = reqState.findIndex((s) => s.request.isSuccessful);
  return [
    ...reqState.slice(0, index),
    {
      ...reqState[index],
      folder: insertFileIntoFolder(reqState[index].folder, document, from),
    },
    ...reqState.slice(index + 1),
  ];
}

function renameFolderByUuid(
  folder: Identifiable<Folder>,
  uuid: string,
  newName: string,
): boolean {
  if (folder.uuid === uuid) {
    folder.name = newName;
    return true;
  }
  const folderChildren = folder.children.filter(
    (child): child is Identifiable<Folder> => child.type === "folder",
  );
  return folderChildren.some((child) =>
    renameFolderByUuid(child, uuid, newName),
  );
}

function handleUpdateFolderSuccess(
  state: State,
  { folder, type, namespace }: Boundaries["updateFolder"]["success"],
): State {
  const index = state.findIndex(
    (s) =>
      s.request.isSuccessful && s.namespace === namespace && s.type === type,
  );
  const updatedFolder = state[index].folder;
  renameFolderByUuid(updatedFolder, folder.uuid, folder.name);
  return [
    ...state.slice(0, index),
    {
      ...state[index],
      folder: updatedFolder,
    },
    ...state.slice(index + 1),
  ];
}

function handleCreate(
  state: State,
  {
    namespace,
    type,
    document,
    folder,
  }: Boundaries["createDocument"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          folder: replaceDocument(state[index].folder, document, folder),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleCopy(
  state: State,
  { namespace, type, document, folder }: Boundaries["copyDocument"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          folder: replaceDocument(state[index].folder, document, folder),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleUpdate(
  state: State,
  {
    namespace,
    type,
    folder,
    document,
  }: Boundaries["updateDocument"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === type,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          folder: updateDocument(state[index].folder, document, folder),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrievePrivateDocumentsRequested]: handleRequest,
    [events.retrievePrivateDocumentsSucceeded]: handleSuccess,
    [events.retrievePrivateDocumentsFailed]: handleFailure,
    [events.uploadDocumentSucceeded]: handleUpload,
    [events.createFolderSucceeded]: handleCreateFolder,
    [events.removeFolderSucceeded]: handleRemoveFolder,
    [events.removeDocumentSucceeded]: handleRemoveDocument,
    [events.moveDocumentRequested]: handleMoveDocumentRequest,
    [events.moveDocumentFailed]: handleMoveDocumentFailure,
    [events.updateFolderSucceeded]: handleUpdateFolderSuccess,
    [events.createDocumentSucceeded]: handleCreate,
    [events.copyDocumentSucceeded]: handleCopy,
    [events.updateDocumentSucceeded]: handleUpdate,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
